// React component

import { motion } from "framer-motion";
import { Shield, CheckCircle, Lock, Eye, AlertCircle } from "lucide-react";
import { useState, useEffect } from "react";

interface SecurityStatus {
  id: number;
  icon: React.ElementType;
  label: string;
  status: "success" | "warning" | "error";
  message: string;
}

const SecurityShield = () => {
  const [securityStatuses] = useState<SecurityStatus[]>([
    {
      id: 1,
      icon: Lock,
      label: "Encryption",
      status: "success",
      message: "256-bit encryption active",
    },
    {
      id: 2,
      icon: Eye,
      label: "Monitoring",
      status: "success",
      message: "Real-time threat detection",
    },
    {
      id: 3,
      icon: AlertCircle,
      label: "Threats",
      status: "success",
      message: "No threats detected",
    },
  ]);

  const [pulseSize, setPulseSize] = useState(1);

  useEffect(() => {
    const interval = setInterval(() => {
      setPulseSize((prev) => (prev === 1 ? 1.1 : 1));
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status: SecurityStatus["status"]) => {
    switch (status) {
      case "success":
        return "text-green-500";
      case "warning":
        return "text-yellow-500";
      case "error":
        return "text-red-500";
      default:
        return "text-muted-foreground";
    }
  };

  return (
    <div
      className="relative w-full max-w-md mx-auto p-6 rounded-xl overflow-hidden"
      data-oid="c901auz"
    >
      {/* Gradient Background */}
      <div
        className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-green-500/5 backdrop-blur-sm"
        data-oid="tmpp3qn"
      />

      <div className="relative flex flex-col items-center" data-oid="::jmh7q">
        {/* Title */}
        <motion.h3
          className="text-xl font-semibold mb-6 text-center text-foreground"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          data-oid="bt18vu8"
        >
          Security Status
        </motion.h3>

        {/* Animated Shield */}
        <motion.div
          className="relative mb-8 scale-110"
          animate={{ scale: pulseSize }}
          transition={{ duration: 1, ease: "easeInOut" }}
          data-oid="jaui2n4"
        >
          {/* Outer glow */}
          <motion.div
            className="absolute inset-0 blur-xl bg-blue-500/20 rounded-full"
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.6, 0.3],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            data-oid=":zqd5_2"
          />

          <motion.div
            className="absolute inset-0 text-blue-500"
            animate={{
              opacity: [0.2, 0.5, 0.2],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            data-oid="b39vyzn"
          >
            <Shield className="h-28 w-28" data-oid="3ui_dk-" />
          </motion.div>

          <Shield
            className="h-28 w-28 text-blue-500 relative z-10"
            data-oid="ij4wg-x"
          />

          <motion.div
            className="absolute inset-0 flex items-center justify-center"
            animate={{ rotate: 360 }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            data-oid="6-m2:5d"
          >
            <div
              className="w-full h-full rounded-full border-4 border-blue-500/20 border-dashed"
              data-oid="bkfhvw:"
            />
          </motion.div>

          <motion.div
            className="absolute inset-0 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            data-oid="hx:n1s_"
          >
            <CheckCircle
              className="h-10 w-10 text-green-500"
              data-oid="iy.7f62"
            />
          </motion.div>
        </motion.div>

        {/* Security Status List */}
        <div className="space-y-4 w-full" data-oid="lugag5v">
          {securityStatuses.map((status, index) => {
            const Icon = status.icon;
            return (
              <motion.div
                key={status.id}
                className="flex items-center gap-4 bg-card/40 backdrop-blur-sm p-4 rounded-lg border border-border/50 hover:border-border/80 transition-colors"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
                data-oid="moefqj."
              >
                <div
                  className={`p-2 rounded-lg bg-card/50 ${getStatusColor(status.status)}`}
                  data-oid="7fcxdb5"
                >
                  <Icon className="h-5 w-5" data-oid="acudbyr" />
                </div>
                <div className="flex-1" data-oid="_e4spdj">
                  <p
                    className="text-sm font-medium text-foreground"
                    data-oid="i:ha5l4"
                  >
                    {status.label}
                  </p>
                  <p
                    className="text-xs text-muted-foreground/80"
                    data-oid="ca3:04k"
                  >
                    {status.message}
                  </p>
                </div>
                <motion.div
                  className={`h-2.5 w-2.5 rounded-full ${
                    status.status === "success"
                      ? "bg-green-500"
                      : status.status === "warning"
                        ? "bg-yellow-500"
                        : "bg-red-500"
                  }`}
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.5, 1, 0.5],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  data-oid="-_sjiy1"
                />
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SecurityShield;
